import { Add as AddIcon } from '@mui/icons-material';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    ListSubheader,
    MenuItem,
    Tab,
    Tabs,
    TextField,
    Tooltip,
} from '@mui/material';
import { JSX, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { addMinutes } from '../../../library/dateTimeHelper';
import { DIARY_START_STEP_MINUTES, freeStartTimesOn } from '../../../library/freeSlotTimes';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { ENTRY_MINUTES_BOUNDS, numericSlotProps } from '../../../library/validationRules';
import { CreateAppointmentRequest } from '../../../library/models/appointment';
import { IPatient } from '../../../library/models/clinic';
import { memberFullName } from '../../../library/membershipDisplay';
import { planLabel } from '../../../library/purchaseDisplay';
import { bookableSessions, IPatientPurchase, isUsable } from '../../../library/models/patientPurchase';
import { EmptySlotResponse } from '../../../library/models/appointment';
import { IService } from '../../../library/models/service';
import { ITherapist } from '../../../library/models/therapist';
import { createNewEntrySchema, latestEndFor, NewEntryFormValues } from './newEntrySchema';
import ResourcesIds from '../../../resources/ResourcesIds';
import { AppDateTimePicker } from '../../pickers/AppDateTimePicker';
import { useIsCompact } from '../../../library/useIsCompact';
import { useDebouncedValue } from '../../../library/useDebouncedValue';
import { useReactInfiniteQuery, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import {
    addPatientButtonStyles,
    fieldStyles,
    patientPickerStyles,
    patientRowStyles,
    tabsStyles,
} from './newEntryDialog.styles';

/**
 * How many patients the picker holds at once.
 *
 * It is a search box rather than a list of everyone: the endpoint pages, and a therapist
 * with years of caseload behind them has more patients than any dropdown should hold. Ten
 * is what fits in the popup without scrolling, and the term narrows the rest.
 */
const PATIENT_PICKER_PAGE_SIZE = 10;

/** A patient is their name, or their email address until they have given one. */
const patientLabel = (patient: IPatient): string =>
    memberFullName(patient) || patient.emailAddress;

interface NewEntryDialogProps {
    open: boolean;
    /** The day the grid is showing, so a new entry lands where the therapist is looking. */
    day: string;
    /**
     * The empty half hour the therapist clicked on the grid, as `<date>T<HH:mm>`.
     *
     * Both the seed *and* the reason the hour list is not asked for -- see `fromSlot` below.
     * Null whenever the dialog was opened any other way, which leaves everything exactly as
     * it was.
     */
    startAt?: string | null;
    /** Whoever is filling the form in: the day this lands in unless a colleague is named. */
    therapistId: number;
    /**
     * Their name, for the one option the list is guaranteed to hold. The roster supplies every
     * other label, and this is what keeps the field from opening on a blank row when that
     * request has not answered or has failed.
     */
    therapistName: string;
    isSaving: boolean;
    onClose: () => void;
    /**
     * @param bookedFor the colleague the entry was handed to, when it was not kept. The
     *                  page above says so rather than claiming it went into this day,
     *                  which is the one thing it cannot then show.
     */
    onConfirm: (request: CreateAppointmentRequest, bookedFor?: string) => void;
    /**
     * *Add patient* was pressed. Carries nothing on purpose: guessing a new patient's name
     * from half a search was more often wrong than helpful, so the form opens empty.
     */
    onAddPatient: () => void;
    /**
     * Somebody just created from that dialog. Selected here, and kept in the options until
     * the next search, because the server cannot yet find them: `mine` is
     * EXISTS(evaluations) OR EXISTS(purchases), and a new patient is in neither.
     */
    newPatient?: IPatient | null;
}

/**
 * The two things a therapist adds to their own day.
 *
 * <b>Booking a patient in</b> covers the phone call and the front desk -- the patient's own
 * wizard is for patients, and a clinic that could only be booked by the person attending
 * would be a strange clinic.
 *
 * <b>Blocking out time</b> is lunch, leave, training, a meeting, the room being used for
 * something else. It is the same table and the same rows, with no patient on them, and it
 * exists because slot finding subtracts *everything that occupies time* from the working
 * day. Time the therapist is not free in, that is not written down, is time the booking
 * wizard will offer to somebody.
 *
 * Two tabs rather than two dialogs because they differ only in what they leave out, exactly
 * as the request does.
 *
 * <b>Neither of them need be this therapist's.</b> A therapist takes the phone call and books
 * whatever the patient asks for, in whoever's day it belongs in -- and marks a colleague's
 * leave while they are at it. So the first thing the form asks is <i>whose day</i>, seeded
 * with the person filling it in and changeable on both tabs. It used to ask that only when
 * the caller was not mapped to the chosen service, which left the ordinary case -- a
 * colleague they both give -- impossible to say, and silently landed it in the caller's own
 * day. The API has always permitted the handover: {@code therapistId} comes from the request
 * and is never compared to the caller, and {@code requireCanGive} checks the discipline for a
 * patient booking themselves alone. Who gives what survives as a heading over the list and a
 * line under it, said rather than enforced.
 *
 * <b>And the hour offered is one that person is free in.</b> The list is
 * {@code POST /appointments/empty-slots} for the chosen therapist, the chosen day and the
 * length being booked -- asked for with {@code includeOutsideProgram}, so it is the whole day
 * minus what already occupies it rather than the clinic's opening hours. That distinction is
 * the whole reason this control can be a list at all: bounded by the program it would refuse
 * the emergency at eight in the evening, and unbounded it was every quarter hour of the day
 * with no idea whether anybody was free in any of them.
 */
const DEFAULT_VALUES: NewEntryFormValues = {
    isBlock: false,
    patientId: '',
    choice: '',
    therapistId: '',
    startsAt: '',
    endsAt: '',
    minutes: '60',
};

/**
 * How long a block has to be free at its start to be offered as one.
 *
 * Not the block's whole length, which is what a booking asks for: a block ends wherever
 * "Until" says, which can be days away, and no day's walk holds a window that long. So the
 * list offers every moment the therapist is free in -- five minutes, the shortest thing the
 * grid draws -- and the API's overlap guard judges the whole span on the way in, refusing
 * a block laid over a booking in words the snackbar prints.
 */
const BLOCK_START_WINDOW_MINUTES = ENTRY_MINUTES_BOUNDS.min;

/** A block's end when nothing has said otherwise: an hour, which is what the minutes box offered. */
const DEFAULT_BLOCK_MINUTES = 60;

/** The hour the form opens on, before the API has said which hours are free. */
const SEED_TIME = '09:00';

export const NewEntryDialog = (props: NewEntryDialogProps): JSX.Element => {
    const { t: translate, i18n } = useTranslation();
    const isCompact = useIsCompact();

    const schema = useMemo(() => createNewEntrySchema(translate), [translate]);

    const {
        register, control, handleSubmit, reset, setValue, formState: { errors, isValid },
    } = useValidatedForm<NewEntryFormValues>(schema, { defaultValues: DEFAULT_VALUES });

    // The queries below key off these, so the whole draft is watched rather than each field.
    const { isBlock, patientId, choice, therapistId, startsAt, endsAt, minutes } =
        useWatch({ control }) as NewEntryFormValues;

    // Reopened on the day being looked at, never on whatever it held last time.
    useEffect(() => {
        if (props.open) {
            reset({
                ...DEFAULT_VALUES,
                // Whoever is filling the form in, until they name somebody else. Seeded here
                // rather than left blank so the slot query has a therapist to ask about from
                // the first render, and so the ordinary case needs no answer at all.
                therapistId: String(props.therapistId),
                // The clicked slot when there was one; otherwise the guess the free-hour
                // list is there to correct.
                startsAt: props.startAt ?? `${props.day}T${SEED_TIME}`,
            });
            // The picker's own two pieces of state are outside the form, so the reset above
            // does not reach them -- and a stale name in the box would name a patient the
            // form no longer holds.
            setSelectedPatient(null);
            setPatientQuery('');
            setJustCreated(null);
        }
    }, [props.open, props.day, props.startAt, props.therapistId, reset]);

    // What is typed into the picker, and who it settled on. Both are local: the form holds
    // an id, and the box has to be able to show a name for it.
    const [patientQuery, setPatientQuery] = useState('');
    const [selectedPatient, setSelectedPatient] = useState<IPatient | null>(null);
    const debouncedPatientQuery = useDebouncedValue(patientQuery);

    // Searched on the server, one page at a time. This used to ask for every patient the
    // therapist has and filter the array in the box -- which is the whole reason the
    // endpoint now takes a term.
    const { data: patientPages, isFetching: isFetchingPatients } = useReactInfiniteQuery<IPatient>(
        services.users.getPatients(PATIENT_PICKER_PAGE_SIZE, {
            // An empty box offers the caller's own patients, which is the shortlist worth
            // showing unprompted. A typed term searches the whole clinic instead: a
            // colleague's patient is ordinary front-desk work to book, and somebody added
            // a moment ago belongs to nobody at all -- `mine` is the two tables they are
            // by definition absent from.
            mine: !debouncedPatientQuery.trim(),
            search: debouncedPatientQuery,
        }),
        { enabled: props.open && !isBlock }
    );

    const patients = useMemo(
        () => patientPages?.pages.flatMap(page => page.items) ?? [],
        [patientPages]
    );

    /**
     * Held only until the keyboard starts a new search.
     *
     * Merging `selectedPatient` instead would pin a name at the top of every later search,
     * matching or not.
     */
    const [justCreated, setJustCreated] = useState<IPatient | null>(null);

    const options: IPatient[] = useMemo(
        () => (justCreated && !patients.some(patient => patient.id === justCreated.id)
            ? [justCreated, ...patients]
            : patients),
        [patients, justCreated]
    );

    /** Everything picking a patient means, in one place, for the two callers that mean it. */
    const choosePatient = useCallback((patient: IPatient | null): void => {
        setSelectedPatient(patient);
        setPatientQuery(patient ? patientLabel(patient) : '');
        setValue('patientId', patient ? String(patient.id) : '', { shouldValidate: true });
        // The plans on offer belong to whoever was chosen before.
        setValue('choice', '', { shouldValidate: true });
        // The therapist is deliberately kept. It is no longer a per-service handover that
        // stops meaning anything when the service changes -- it is whose day this is, which a
        // different patient does not answer differently, and clearing it would put an
        // explicit choice back to the caller behind their back.
    }, [setValue]);

    // After the reset-on-open effect above on purpose: a reset landing on top of this would
    // clear the patient the dialog was reopened to select.
    const { newPatient } = props;

    useEffect(() => {
        if (newPatient) {
            setJustCreated(newPatient);
            choosePatient(newPatient);
        }
    }, [newPatient, choosePatient]);

    const { data: bookable } = useReactQuery<IService[]>(
        services.services.getServices(),
        { enabled: props.open && !isBlock }
    );

    /** What this patient already has, so a booking draws down a package rather than a new sale. */
    const { data: purchases } = useReactQuery<IPatientPurchase[]>(
        services.patientPurchases.getPurchasesForPatient(Number(patientId) || 0),
        { enabled: props.open && !isBlock && !!patientId }
    );

    const drawableOn: IPatientPurchase[] = (purchases ?? []).filter(
        purchase => isUsable(purchase) && bookableSessions(purchase) > 0
    );

    const chosenPurchase: IPatientPurchase | undefined = choice.startsWith('plan:')
        ? drawableOn.find(purchase => `plan:${purchase.id}` === choice)
        : undefined;

    const chosenService: IService | undefined = choice.startsWith('service:')
        ? (bookable ?? []).find(service => `service:${service.id}` === choice)
        : undefined;

    /**
     * The catalogue line a plan's sessions come from.
     *
     * The option the purchase records and nothing else. The old name-and-duration pair
     * behind it has been removed rather than kept as a fallback: `bookable` is singles
     * only, so a package's own line is never in it, and the pair then matched the *single*
     * line of the same discipline -- which put that single's id on the row as
     * `catalogOptionId` and named the plan after it in the menu.
     *
     * A miss is survivable: the purchase still names the service on the row, same as it
     * always has when nothing points the other way.
     */
    const catalogueLineFor = (purchase: IPatientPurchase): IService | undefined =>
        (bookable ?? []).find(service => service.id === purchase.sourceCatalogOptionId);

    const planService: IService | undefined =
        chosenPurchase && catalogueLineFor(chosenPurchase);

    const knowsItsLength: boolean = !!chosenService || !!chosenPurchase;

    /**
     * The catalogue option to ask *who gives this* about -- a plan's own line rather than
     * the single it resembles, which is the same id the patient's wizard asks with. A plan
     * that names none leaves nothing to ask, and the step below is simply absent then: a
     * lookup that is allowed to miss must not gate the form.
     */
    const askAbout: number | undefined =
        chosenService?.id ?? chosenPurchase?.sourceCatalogOptionId ?? undefined;

    const { data: qualified, isSuccess: knowsWhoGivesIt } = useReactQuery<ITherapist[]>(
        services.therapists.getTherapists(askAbout ?? 0),
        { enabled: props.open && !isBlock && !!askAbout }
    );

    /**
     * Who the clinic has mapped to the chosen discipline -- the same list the patient's
     * wizard offers, so the two cannot disagree about who gives what.
     *
     * It no longer decides *whether* a therapist is asked for, only how the list is sorted
     * and what is said under it: the API allows staff to book an unmapped colleague, and an
     * unfinished mapping must not stop work.
     */
    const qualifiedIds: number[] = (qualified ?? []).map(one => one.id);

    /**
     * Everybody at this clinic, because whose day this goes into is a different question from
     * who may give the service. Somebody mapped to no discipline still has a diary.
     */
    const { data: roster } = useReactQuery<ITherapist[]>(
        services.therapists.getClinicTherapists(),
        { enabled: props.open }
    );

    /**
     * The caller is always on the list, whatever the roster did.
     *
     * A lookup that is allowed to miss must never gate a step: in flight, or refused, the
     * field still holds the one therapist the dialog knows for certain -- the person filling
     * it in, whose id came in as a prop. Without this the form would open invalid, with its
     * seeded value naming nobody in the options.
     */
    const everybody: ITherapist[] = useMemo(() => {
        const listed: ITherapist[] = roster ?? [];

        return listed.some(one => one.id === props.therapistId)
            ? listed
            : [
                {
                    id: props.therapistId,
                    firstName: props.therapistName,
                    lastName: '',
                    emailAddress: '',
                },
                ...listed,
            ];
    }, [roster, props.therapistId, props.therapistName]);

    /**
     * Whoever gives the chosen service first, everybody else after -- and headings only when
     * there are two groups to tell apart, the rule the service select above follows too.
     */
    const gives: ITherapist[] = everybody.filter(one => qualifiedIds.includes(one.id));
    const others: ITherapist[] = everybody.filter(one => !qualifiedIds.includes(one.id));
    const grouped: boolean = !isBlock && knowsWhoGivesIt && gives.length > 0 && others.length > 0;

    /** Whose day this is going into. Never undefined once the form has been reset. */
    const chosen: ITherapist | undefined =
        everybody.find(one => String(one.id) === therapistId);

    const chosenId: number = Number(therapistId) || 0;

    /**
     * Said, not enforced: the API allows it and the mapping may simply be unfinished.
     *
     * The name rather than a flag, because there is nothing worth saying when the list has
     * not answered and nobody to name it about.
     */
    const unqualifiedName: string = !isBlock && knowsWhoGivesIt && chosen
        && !qualifiedIds.includes(chosen.id)
        ? memberFullName(chosen)
        : '';

    // A service or plan's own length wins over the box, so a slot matches what was sold. A
    // block asks only for a free moment to start in -- see BLOCK_START_WINDOW_MINUTES.
    const runsFor: number = isBlock
        ? BLOCK_START_WINDOW_MINUTES
        : chosenService?.duration
            ?? chosenPurchase?.durationMinutes
            ?? (Number(minutes) || 60);

    /** The date half alone, which is what a day's worth of free windows is asked about. */
    const day: string = startsAt.slice(0, 10);

    /**
     * Opened on a slot that was already empty, in the caller's own column.
     *
     * The one exemption from the rule below, and it is narrow twice over. The therapist
     * pointed at the gap, so there is nothing left for slot discovery to discover -- and the
     * walk answers a *different* question from the one they asked, "where does something
     * this long fit", which would move the hour they clicked or clear it the moment a
     * service made the entry too long for the gap.
     *
     * It ends the instant the entry is handed to a colleague: an empty slot is a fact about
     * *this* column and says nothing about theirs, so the list, the strict picker and the
     * clear-rather-than-move behaviour all come back exactly as documented.
     *
     * Nothing is being waved through. `requireAvailable` never applied to a staff booking in
     * the first place -- the API applies it under `patientIsCaller` alone -- and
     * `guardOverlap` still refuses a real clash, in words the snackbar already prints.
     */
    const fromSlot: boolean = !!props.startAt && Number(therapistId) === props.therapistId;

    /**
     * When the chosen therapist is free, on the chosen day, for as long as this will run.
     *
     * Two flags are what make this usable as the *only* list of hours, and the server ignores
     * both for a patient caller, so the wizard's own list is unaffected.
     * `includeOutsideProgram`: without it the answer stops at the clinic's closing time and a
     * therapist could not write down an evening emergency at all. `startStepMinutes`: without
     * it the walk tiles windows back to back, so an empty day offers 09:00 and 10:00 and no
     * way at all to say half past nine.
     *
     * Keyed on all three inputs by `createPostQuery`, so changing the therapist, the day or
     * the length asks again rather than showing the previous answer.
     */
    const { data: slots, isFetching: isFetchingSlots } = useReactQuery<EmptySlotResponse[]>(
        services.appointments.getEmptySlots(day, day, runsFor, chosenId, {
            includeOutsideProgram: true,
            startStepMinutes: DIARY_START_STEP_MINUTES,
            // The line the room is read from, the same one the API resolves on create. A
            // block of leave needs no room, whatever was picked before switching tabs.
            serviceId: isBlock ? undefined : askAbout,
        }),
        { enabled: props.open && !fromSlot && !!chosenId && !!day && runsFor > 0 }
    );

    const freeTimes: string[] = useMemo(
        () => freeStartTimesOn(slots, day), [slots, day]
    );

    /**
     * The held hour is reconciled with the answer, in the one direction each case allows.
     *
     * An untouched seed moves to the first free time -- 09:00 is a guess made before anybody
     * asked, and correcting a guess is not overriding a choice. Anything else is *cleared*
     * back to the bare day rather than replaced: the person picked that hour, and quietly
     * moving them to a different one is worse than asking again. The schema refuses a day
     * with no hour on it, so Add goes flat and says why.
     */
    useEffect(() => {
        // Nothing to reconcile against when no list was asked for, and the held hour is the
        // one the therapist pointed at -- neither a guess to correct nor a clash to clear.
        if (!props.open || fromSlot || isFetchingSlots || !slots || !day) {
            return;
        }

        const held: string = startsAt.slice(11, 16);

        if (held && freeTimes.includes(held)) {
            return;
        }

        const untouched: boolean = held === SEED_TIME;

        if (untouched && freeTimes.length > 0) {
            setValue('startsAt', `${day}T${freeTimes[0]}`, { shouldValidate: true });
        } else if (held) {
            setValue('startsAt', `${day}T`, { shouldValidate: true });
        }
    }, [props.open, fromSlot, isFetchingSlots, slots, freeTimes, startsAt, day, setValue]);

    /**
     * A block's end follows its start until it means something.
     *
     * Seeded an hour on whenever there is a whole start and the end is missing or no longer
     * after it -- the first time a block is chosen, and whenever the start is moved past it.
     * An end that is still after the start is the therapist's answer and stays where it is,
     * even as the start moves: somebody setting a week of leave and then correcting its first
     * morning has not changed when they are back.
     */
    useEffect(() => {
        if (!props.open || !isBlock || !/T\d{2}:\d{2}/.test(startsAt)) {
            return;
        }

        const start: string = startsAt.slice(0, 16);

        if (!endsAt || endsAt.slice(0, 16) <= start) {
            setValue('endsAt', addMinutes(start, DEFAULT_BLOCK_MINUTES).slice(0, 16),
                { shouldValidate: true });
        }
    }, [props.open, isBlock, startsAt, endsAt, setValue]);

    const onValid = (values: NewEntryFormValues): void => {
        const serviceIdToSend: number | undefined = chosenService?.id ?? planService?.id;
        const when: string = values.startsAt;
        const goesTo: number = Number(values.therapistId);

        props.onConfirm(
            isBlock
                ? {
                    // Leave is handed over exactly as a booking is now: a therapist marking
                    // a colleague's afternoon out is the same front-desk act, and the API
                    // has never distinguished the two.
                    therapistId: goesTo,
                    startsAt: `${when}:00`,
                    endsAt: `${values.endsAt.slice(0, 16)}:00`,
                }
                : {
                    patientId: Number(patientId),
                    therapistId: goesTo,
                    ...(serviceIdToSend ? { serviceId: serviceIdToSend } : {}),
                    ...(chosenPurchase ? { purchaseId: chosenPurchase.id } : {}),
                    startsAt: `${when}:00`,
                    endsAt: addMinutes(when, runsFor),
                },
            // Named whenever this is not the caller's own day, so the page says where it went
            // rather than claiming an entry its grid will not draw.
            goesTo === props.therapistId || !chosen ? undefined : memberFullName(chosen)
        );
    };

    return (
        <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="xs" fullScreen={isCompact}>
            <DialogTitle>{translate(ResourcesIds.MY_CALENDAR_ADD)}</DialogTitle>

            <DialogContent>
                <Tabs
                    sx={tabsStyles}
                    value={isBlock ? 1 : 0}
                    onChange={(_, value) => setValue('isBlock', value === 1, { shouldValidate: true })}
                >
                    <Tab label={translate(ResourcesIds.MY_CALENDAR_ADD_BOOKING)} />
                    <Tab label={translate(ResourcesIds.MY_CALENDAR_ADD_BLOCK)} />
                </Tabs>

                {!isBlock && (
                    <>
                        <Box sx={patientRowStyles}>
                            <Autocomplete
                                fullWidth
                                size="small"
                                sx={patientPickerStyles}
                                options={options}
                                value={selectedPatient}
                                inputValue={patientQuery}
                                getOptionLabel={patientLabel}
                                isOptionEqualToValue={(option: IPatient, value: IPatient) =>
                                    option.id === value.id}
                                // The server has already decided what matches, and it knows
                                // things this cannot: it ignores diacritics, and it searches
                                // email addresses and phone numbers as well as names -- but not,
                                // deliberately, the name of a therapist a patient belongs to.
                                // MUI's own filter would throw the server's matches away again,
                                // so it is told to keep every option it was given.
                                filterOptions={(all: IPatient[]) => all}
                                // Gated on this, so "no matches" cannot flash mid-fetch.
                                loading={isFetchingPatients}
                                loadingText={translate(ResourcesIds.LOADING_TEXT)}
                                noOptionsText={translate(ResourcesIds.PATIENTS_NO_MATCHES_TITLE)}
                                onInputChange={(_event: SyntheticEvent, next: string, reason: string) => {
                                    // A pick sets the box to the chosen name; only the keyboard
                                    // should start a new search.
                                    if (reason === 'input') {
                                        setPatientQuery(next);
                                        // Whoever was just created stops being pinned to the top
                                        // the moment somebody looks for somebody else.
                                        setJustCreated(null);
                                    }
                                }}
                                onChange={(_event: SyntheticEvent, patient: IPatient | null) =>
                                    choosePatient(patient)}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label={translate(ResourcesIds.MY_CALENDAR_ADD_PATIENT)}
                                        {...fieldProps(errors, 'patientId')}
                                    />
                                )}
                            />

                            {/* Always on offer rather than only once a search has come back
                                empty: a therapist usually knows somebody is new before typing
                                their name, and should not have to prove it to the picker first.
                                Icon-only on a phone, where the label would squeeze the field. */}
                            {isCompact ? (
                                <Tooltip title={translate(ResourcesIds.ADD_PATIENT_ACTION)}>
                                    <IconButton
                                        color="primary"
                                        aria-label={translate(ResourcesIds.ADD_PATIENT_ACTION)}
                                        onClick={() => props.onAddPatient()}
                                    >
                                        <AddIcon />
                                    </IconButton>
                                </Tooltip>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    sx={addPatientButtonStyles}
                                    onClick={() => props.onAddPatient()}
                                >
                                    {translate(ResourcesIds.ADD_PATIENT_ACTION)}
                                </Button>
                            )}
                        </Box>

                        {/* One pick answers what a service and a purchase used to answer
                            separately: a plan is a service too, just one that has already
                            been paid for. Grouped only once there is a plan to group against --
                            with none, this is the flat list it always was. */}
                        <TextField
                            select
                            fullWidth
                            size="small"
                            sx={fieldStyles}
                            label={translate(ResourcesIds.MY_CALENDAR_ADD_SERVICE)}
                            value={choice}
                            // The therapist is deliberately left alone. It used to be cleared
                            // here, because it named the colleague this *service* was being
                            // handed to and meant nothing once the service changed. It now
                            // names whose day the entry goes into, which a different service
                            // does not re-answer -- and clearing it would take the hour list
                            // away with it, since that list is asked about a named therapist.
                            onChange={event =>
                                setValue('choice', event.target.value, { shouldValidate: true })}
                            // The hint is what this field says when there is nothing wrong,
                            // so fieldProps takes it as the fallback rather than beside it.
                            {...fieldProps(errors, 'choice',
                                chosenPurchase
                                    ? translate(ResourcesIds.MY_CALENDAR_ADD_DRAWS_DOWN_HINT, {
                                        remaining: bookableSessions(chosenPurchase) - 1,
                                    })
                                    : chosenService
                                        ? translate(ResourcesIds.MY_CALENDAR_ADD_NO_PURCHASE)
                                        : ' ')}
                        >
                            {drawableOn.length > 0
                                ? [
                                    <ListSubheader key="plans-header">
                                        {translate(ResourcesIds.MY_CALENDAR_ADD_PLANS)}
                                    </ListSubheader>,
                                    ...drawableOn.map(purchase => (
                                        <MenuItem key={`plan:${purchase.id}`} value={`plan:${purchase.id}`}>
                                            {translate(ResourcesIds.APPOINTMENT_PURCHASE_OPTION, {
                                                service: planLabel(purchase, translate, i18n.language),
                                                remaining: bookableSessions(purchase),
                                            })}
                                        </MenuItem>
                                    )),
                                    <ListSubheader key="services-header">
                                        {translate(ResourcesIds.MY_CALENDAR_ADD_NO_PURCHASE)}
                                    </ListSubheader>,
                                ]
                                : []}
                            {(bookable ?? []).map(service => (
                                <MenuItem key={`service:${service.id}`} value={`service:${service.id}`}>
                                    {`${service.nameRo} · ${service.durationLabelRo}`}
                                </MenuItem>
                            ))}
                        </TextField>

                    </>
                )}

                {/* Both tabs, always. Whose day this goes into is the question the rest of the
                    form hangs off -- it decides which hours may even be offered below -- and
                    it is not a handover the caller only sometimes makes. Grouped by who gives
                    the chosen service when that is known and splits the list, which is a
                    heading rather than a rule: the API lets staff book an unmapped colleague,
                    and a clinic part-way through filling its mapping in must not be stopped. */}
                <TextField
                    select
                    fullWidth
                    size="small"
                    sx={fieldStyles}
                    label={translate(ResourcesIds.ADD_PURCHASE_THERAPIST_LABEL)}
                    value={therapistId}
                    onChange={event =>
                        setValue('therapistId', event.target.value, { shouldValidate: true })}
                    {...fieldProps(errors, 'therapistId', unqualifiedName
                        ? translate(ResourcesIds.MY_CALENDAR_ADD_THERAPIST_NOT_QUALIFIED, {
                            name: unqualifiedName,
                        })
                        : ' ')}
                >
                    {grouped
                        ? [
                            <ListSubheader key="gives-header">
                                {translate(ResourcesIds.MY_CALENDAR_ADD_THERAPIST_GIVES)}
                            </ListSubheader>,
                            ...gives.map(one => (
                                <MenuItem key={one.id} value={String(one.id)}>
                                    {memberFullName(one)}
                                </MenuItem>
                            )),
                            <ListSubheader key="others-header">
                                {translate(ResourcesIds.MY_CALENDAR_ADD_THERAPIST_OTHERS)}
                            </ListSubheader>,
                            ...others.map(one => (
                                <MenuItem key={one.id} value={String(one.id)}>
                                    {memberFullName(one)}
                                </MenuItem>
                            )),
                        ]
                        : everybody.map(one => (
                            <MenuItem key={one.id} value={String(one.id)}>
                                {memberFullName(one)}
                            </MenuItem>
                        ))}
                </TextField>

                {isBlock && (
                    <Alert severity="info" sx={fieldStyles}>
                        {translate(ResourcesIds.MY_CALENDAR_ADD_BLOCK_HINT)}
                    </Alert>
                )}

                {/* Still unbounded by the clinic's program -- the list is the whole day, so
                    an emergency at eight in the evening is on it. What it leaves out is time
                    the chosen therapist is already in, which the API would refuse anyway.
                    A control that has gone empty must say why, or it reads as broken, so the
                    helper line is a count, a wait, or the reason there is nothing. */}
                <Controller
                    name="startsAt"
                    control={control}
                    render={({ field }) => (
                        <AppDateTimePicker
                            label={translate(ResourcesIds.MY_CALENDAR_ADD_STARTS_AT)}
                            timeLabel={translate(ResourcesIds.PICKER_TIME_LABEL)}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            name={field.name}
                            inputRef={field.ref}
                            // No minutesStep: under `strictTime` the list is `timeOptions` and
                            // nothing is generated from a step, so one here would be a number
                            // that never applied. How far apart the offers sit is the API's
                            // answer -- DIARY_START_STEP_MINUTES, sent with the query.
                            // Unconstrained when the hour came off the grid: with no list
                            // asked for there is none to be strict about, so the control
                            // goes back to the ordinary one it is everywhere else. The
                            // helper line goes quiet with it rather than reporting a count
                            // of a list nobody fetched.
                            timeOptions={fromSlot ? undefined : freeTimes}
                            strictTime={!fromSlot}
                            noTimesText={fromSlot
                                ? undefined
                                : translate(ResourcesIds.MY_CALENDAR_ADD_NO_FREE)}
                            sx={fieldStyles}
                            {...fieldProps(errors, 'startsAt', fromSlot
                                ? ' '
                                : isFetchingSlots
                                    ? translate(ResourcesIds.MY_CALENDAR_ADD_TIMES_LOADING)
                                    : freeTimes.length > 0
                                        ? translate(ResourcesIds.MY_CALENDAR_ADD_FREE_COUNT, {
                                            count: freeTimes.length,
                                        })
                                        : translate(ResourcesIds.MY_CALENDAR_ADD_NO_FREE))}
                        />
                    )}
                />

                {/* A block says when it ends rather than how long it runs: it can be a week
                    of leave, which no count of minutes is a sane way to type. Bounded to the
                    days the API allows, and otherwise free -- there is no list here, because
                    the overlap guard judges the whole span when it is added. */}
                {isBlock && (
                    <Controller
                        name="endsAt"
                        control={control}
                        render={({ field }) => (
                            <AppDateTimePicker
                                label={translate(ResourcesIds.MY_CALENDAR_ADD_UNTIL)}
                                // Its own name, not the start's "Time": two fields called the
                                // same thing are one field to a screen reader.
                                timeLabel={translate(ResourcesIds.MY_CALENDAR_ADD_UNTIL_TIME)}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                name={field.name}
                                inputRef={field.ref}
                                minDate={day || undefined}
                                maxDate={day ? latestEndFor(`${day}T00:00`).slice(0, 10) : undefined}
                                sx={fieldStyles}
                                {...fieldProps(errors, 'endsAt')}
                            />
                        )}
                    />
                )}

                {/* Only asked for when nothing else answers it: a service or a plan carries
                    its own length, so typing a second one could only disagree with what was
                    sold. */}
                {!isBlock && !knowsItsLength && (
                    <TextField
                        type="number"
                        fullWidth
                        size="small"
                        sx={fieldStyles}
                        label={translate(ResourcesIds.MY_CALENDAR_ADD_MINUTES)}
                        {...register('minutes')}
                        slotProps={numericSlotProps(ENTRY_MINUTES_BOUNDS)}
                        {...fieldProps(errors, 'minutes')}
                    />
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={props.onClose}>
                    {translate(ResourcesIds.CLOSE_DIALOG)}
                </Button>
                <Button
                    variant="contained"
                    disabled={!isValid || props.isSaving}
                    onClick={handleSubmit(onValid)}
                >
                    {translate(ResourcesIds.MY_CALENDAR_ADD_CONFIRM)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
